<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <i class="fas fa-campground text-indigo-200 text-xl"></i>
          <h2 class="text-base sm:text-lg font-semibold text-gray-100">
            อัปเกรดความจุค่าย
          </h2>
        </div>
        <button
          @click="cancelModal"
          class="p-2 rounded-md text-gray-400 hover:text-indigo-200 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-opacity-50"
          :disabled="isProcessing"
          aria-label="ปิด"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <!-- Loading Overlay -->
      <div v-if="isLoadingAuth || isLoading" class="absolute inset-0 rounded-lg flex items-center justify-center bg-gray-900 bg-opacity-70">
        <div class="animate-spin h-10 w-10 rounded-full border-4 border-indigo-500 border-t-transparent"></div>
      </div>
      <!-- Error -->
      <div v-if="error" class="text-center text-red-300 text-xs sm:text-sm">
        {{ error }}
      </div>
      <!-- Content -->
      <div class="space-y-4">
        <div class="bg-gray-900 rounded-xl p-4 border border-gray-700 text-center">
          <div class="text-sm text-gray-300 mb-1">ความจุปัจจุบัน</div>
          <div class="text-xl font-bold text-white">{{ currentCapacity }}</div>
          <div v-if="nextCapacity" class="text-sm text-gray-300 mt-2 mb-1">ความจุถัดไป</div>
          <div v-if="nextCapacity" class="text-xl font-bold text-indigo-200">{{ nextCapacity }}</div>
        </div>
        <div v-if="!nextCapacity" class="text-center text-sm text-red-300 p-4 bg-gray-900 rounded-xl border border-gray-700">
          ความจุสูงสุดแล้ว ไม่สามารถอัปเกรดได้
        </div>
        <div v-else class="space-y-4">
          <div class="text-center text-sm text-yellow-300">
            ค่าอัปเกรด: {{ diamondCost }} เพชร หรือ {{ cashCost }} cash
          </div>
          <div class="flex justify-center gap-4">
            <label class="flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-md border border-gray-700 cursor-pointer hover:border-indigo-500 transition-all">
              <input type="radio" v-model="paymentMethod" value="diamond" class="form-radio text-indigo-600"> 
              <span class="text-sm">เพชร ({{ diamond }})</span>
            </label>
            <label class="flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-md border border-gray-700 cursor-pointer hover:border-indigo-500 transition-all">
              <input type="radio" v-model="paymentMethod" value="cash" class="form-radio text-indigo-600"> 
              <span class="text-sm">Cash ({{ cash }})</span>
            </label>
          </div>
        </div>
      </div>
      <!-- Actions -->
      <div class="space-y-2">
        <div v-if="nextCapacity">
          <button
            @click="confirmUpgrade"
            :disabled="isProcessing || !canAfford"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md text-sm font-medium border border-blue-700 transition-all duration-200 disabled:opacity-60 flex items-center justify-center"
          >
            <span v-if="!isProcessing">ยืนยันอัปเกรด</span>
            <div v-else class="animate-spin h-5 w-5 rounded-full border-2 border-t-transparent"></div>
          </button>
        </div>
        <button
          @click="cancelModal"
          class="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-md text-sm font-medium border border-gray-600 transition-all duration-200"
        >ยกเลิก</button>
      </div>
      <!-- Messages -->
      <div v-if="successMessage" class="mt-2 text-center text-indigo-300 text-sm">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="mt-2 text-center text-red-300 text-sm">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>
<script>
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import { ref, watch, computed, onUnmounted } from 'vue';

export default {
  name: 'UpgradeCapacityModal',
  props: {},
  emits: ['close', 'updated'],
  setup(_, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const playerStore = usePlayerStore();
    const currentCapacity = ref(10);
    const cash = computed(() => playerStore.userData?.cash || 0);
    const diamond = computed(() => playerStore.characterData?.diamond || 0);
    const isLoading = ref(true);
    const error = ref(null);
    const isProcessing = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    const paymentMethod = ref('diamond');
    const gameActions = projectFunctions.httpsCallable('gameActions');

    let unsubscribeCamp = null;

    const nextCapacity = computed(() => {
      if (currentCapacity.value < 20) return currentCapacity.value + 1;
      return null;
    });

    const diamondCost = computed(() => {
      if (nextCapacity.value <= 15) return 300;
      if (nextCapacity.value <= 20) return 600;
      return 0;
    });

    const cashCost = computed(() => {
      if (nextCapacity.value <= 15) return 100;
      if (nextCapacity.value <= 20) return 200;
      return 0;
    });

    const canAfford = computed(() => {
      return paymentMethod.value === 'diamond' ? diamond.value >= diamondCost.value : cash.value >= cashCost.value;
    });

    watch(
      user,
      async (newUser) => {
        if (newUser) {
          try {
            unsubscribeCamp = projectFirestore.collection('camps').doc(newUser.uid).onSnapshot(
              (doc) => {
                if (doc.exists) {
                  currentCapacity.value = doc.data().capacity || 10;
                } else {
                  error.value = 'ไม่พบข้อมูลค่าย';
                }
                isLoading.value = false;
              },
              (err) => { error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลค่าย: ' + err.message; isLoading.value = false; }
            );
          } catch (err) {
            error.value = 'ไม่สามารถเชื่อมต่อฐานข้อมูลได้: ' + err.message;
            isLoading.value = false;
          }
        } else {
          isLoading.value = false;
          error.value = 'กรุณาล็อกอินก่อน';
        }
      },
      { immediate: true }
    );

    const confirmUpgrade = async () => {
      if (isProcessing.value || !user.value || !nextCapacity.value) return;
      isProcessing.value = true;
      successMessage.value = '';
      errorMessage.value = '';
      try {
        const result = await gameActions({
          action: 'villagerActions',
          subAction: 'upgradeCapacity',
          paymentMethod: paymentMethod.value
        });
        if (result.data.success) {
          successMessage.value = result.data.message || 'อัปเกรดความจุค่ายสำเร็จ';
          emit('updated');
        } else {
          throw new Error(result.data.message || 'การอัปเกรดล้มเหลว');
        }
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการอัปเกรด';
      } finally {
        isProcessing.value = false;
      }
    };

    const cancelModal = () => { emit('close'); };

    onUnmounted(() => {
      if (unsubscribeCamp) unsubscribeCamp();
    });

    return {
      currentCapacity, nextCapacity, cash, diamond, isLoading, isLoadingAuth, error,
      isProcessing, successMessage, errorMessage, paymentMethod,
      diamondCost, cashCost, canAfford,
      confirmUpgrade, cancelModal
    };
  },
};
</script>
<style scoped>
/* Animations */
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }
/* Buttons */
button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }
/* Form Radio */
.form-radio { accent-color: #6366f1; } /* indigo-500 */
</style>